import type { StatusTone } from './statusColors';

/**
 * LOGS DE ENVIO (Admin A2, 26/10/2026, decisões do Heitor: T1, os 12 tipos, o nulo como "Sistema").
 *
 * O `entityType` de `email_logs` é TEXTO LIVRE no banco e o backend não tem mapa de rótulos: quem escreve um envio novo
 * inventa o valor. Os 12 vivos foram enumerados pelo Bastião em produção (26/10); a tela mostrava o valor cru para 7 deles
 * ("procurement_item #16004"). Um valor que nasça depois sai como "Outro (valor)" e nunca cru, para não voltar a parecer
 * rótulo. O nulo (1.350 linhas, 32%) são os alertas do próprio sistema: "Sistema", como já era.
 */
export const ROTULO_DO_TIPO: Record<string, string> = {
  invoice: 'Fatura',
  procurement_item: 'Licitação',
  contract: 'Contrato',
  dispute_session: 'Disputa',
  proposal: 'Proposta',
  portability_term: 'Portabilidade',
  invoice_dunning: 'Cobrança',
  comunicado: 'Comunicado',
  corporate_doc: 'Documento societário',
  user: 'Usuário',
  invoice_dunning_test: 'Cobrança (teste)',
  // O ensaio do comunicado (h1, backend do Bastião, 27/10/2026): purpose interno, cai em "Internos".
  comunicado_teste: 'Comunicado (teste)',
};

export function rotuloDoTipo(tipo: string | null | undefined): string {
  if (!tipo) return 'Sistema';
  return ROTULO_DO_TIPO[tipo] || `Outro (${tipo})`;
}

/**
 * O filtro de tipo: todos os com valor. O nulo (Sistema) fica de fora porque o backend filtra por igualdade, e um
 * "Sistema" que não filtra seria opção mentirosa.
 */
export const TIPOS_DO_FILTRO: Array<{ valor: string; rotulo: string }> = Object.entries(ROTULO_DO_TIPO)
  .map(([valor, rotulo]) => ({ valor, rotulo }));

/**
 * T1: só problema é colorido. O caminho normal (enviado, processado, entregue, aberto, clicado) é neutro; atrasado
 * (deferred, o provedor vai tentar de novo) é âmbar; rejeitado, descartado, spam e falha são vermelhos, e a linha ganha a
 * barra. Status desconhecido sai neutro com o valor, para não inventar gravidade.
 */
const STATUS: Record<string, { rotulo: string; tom: StatusTone }> = {
  sent: { rotulo: 'Enviado', tom: 'neutral' },
  processed: { rotulo: 'Processado', tom: 'neutral' },
  delivered: { rotulo: 'Entregue', tom: 'neutral' },
  opened: { rotulo: 'Aberto', tom: 'neutral' },
  clicked: { rotulo: 'Clicado', tom: 'neutral' },
  deferred: { rotulo: 'Atrasado', tom: 'warning' },
  bounced: { rotulo: 'Rejeitado', tom: 'danger' },
  dropped: { rotulo: 'Descartado', tom: 'danger' },
  spam_report: { rotulo: 'Spam', tom: 'danger' },
  failed: { rotulo: 'Falhou', tom: 'danger' },
};

export function statusDoEnvio(status: string | null | undefined): { rotulo: string; tom: StatusTone } {
  return STATUS[String(status || '')] || { rotulo: String(status || '—'), tom: 'neutral' };
}

/** O tom da barra da linha: só problema tem barra. */
export function barraDoEnvio(status: string | null | undefined): StatusTone | null {
  const { tom } = statusDoEnvio(status);
  return tom === 'neutral' ? null : tom;
}

/** Cartão de problema (Rejeitados, Falhas): a barra vermelha só quando passa de zero. */
export const barraDoProblema = (n: number | null | undefined): StatusTone | null => (Number(n) > 0 ? 'danger' : null);

/**
 * A1 (27/10/2026): a visão padrão é "Para clientes"; o filtro é do SERVIDOR (`publico`), na lista e nos cartões, para a
 * paginação e a contagem baterem. Interno = domínio IPCOM ou purpose interno (critério do Bastião). '' = todos.
 */
export type Publico = 'clientes' | 'internos' | '';
export const VISOES_DO_PUBLICO: Array<{ valor: Publico; rotulo: string }> = [
  { valor: 'clientes', rotulo: 'Para clientes' },
  { valor: 'internos', rotulo: 'Internos' },
  { valor: '', rotulo: 'Todos' },
];

/**
 * B1 (27/10/2026): o cliente da linha vem do registro de origem (fatura, cobrança, contrato, termo de portabilidade,
 * comunicado), com o e-mail só como reserva — antes vinha do e-mail primeiro e, no e-mail cadastrado em vários clientes,
 * saía o cliente de OUTRA linha (caso real: o log 3876, da fatura da FESTVAL, mostrava uma loja diferente conforme o
 * tamanho da página). Quando o cliente veio da reserva e o e-mail é de mais de um cliente, a tela NÃO escolhe um: diz
 * quantos. Envio interno diz "Interno"; sem cliente, "—".
 */
export function clienteDaLinha(log: {
  interno?: boolean | null; clientName?: string | null; clientTradeName?: string | null; clientCompanyName?: string | null;
  origemDoCliente?: string | null; clientesDoEmail?: number | null;
}): { texto: string; sub: string | null; interno: boolean } {
  if (log?.interno) return { texto: 'Interno', sub: null, interno: true };
  const n = Number(log?.clientesDoEmail) || 0;
  if (log?.origemDoCliente === 'email' && n > 1) {
    return { texto: `${n} clientes com este e-mail`, sub: 'o envio não diz de qual', interno: false };
  }
  // Cliente com `name` vazio e só fantasia/razão (o 318 do termo de portabilidade #17, visto na prévia de 27/10).
  const nome = log?.clientName || log?.clientTradeName || log?.clientCompanyName;
  if (!nome) return { texto: '—', sub: null, interno: false };
  return { texto: nome, sub: log.origemDoCliente === 'proposal_nome' ? 'pelo nome da proposta' : null, interno: false };
}

/** C1 (27/10/2026): quem enviou. Os anteriores à coluna não têm registro, e a tela diz isso em vez de deixar vazio. */
export function enviadoPorTexto(e: { tipo?: string | null; nome?: string | null; rotina?: string | null } | null | undefined): string {
  if (e?.tipo === 'pessoa' && e.nome) return e.nome;
  if (e?.tipo === 'automatico') return e.rotina ? `Automático · ${e.rotina}` : 'Automático';
  return 'não registrado';
}

/**
 * E1 (27/10/2026): o cartão Rejeitados conta o que NÃO CHEGOU para o backend (`NAO_CHEGOU` em estatisticas-de-email:
 * rejeitado, descartado e spam), e o clique filtra exatamente por isso — senão o cartão diria 7 e a lista mostraria 4.
 */
export const STATUS_NAO_CHEGOU = 'bounced,dropped,spam_report';

const NAO_CHEGOU = new Set(STATUS_NAO_CHEGOU.split(','));

/**
 * E1: o caminho do envio em texto curto, no lugar das três bolinhas sem legenda (o detalhe só existia no passar do mouse).
 * O problema traz o motivo quando o provedor deu um; o caminho normal diz até onde chegou.
 */
export function caminhoDoEnvio(log: {
  status?: string | null; deliveredAt?: string | null; openedAt?: string | null; openCount?: number | null;
  clickedAt?: string | null; bounceReason?: string | null;
}): { texto: string; tom: StatusTone } {
  const s = String(log?.status || '');
  if (NAO_CHEGOU.has(s) || s === 'failed') {
    const { rotulo } = statusDoEnvio(s);
    return { texto: log.bounceReason ? `${rotulo}: ${log.bounceReason}` : rotulo, tom: 'danger' };
  }
  if (s === 'deferred') return { texto: 'Atrasado: o provedor vai tentar de novo', tom: 'warning' };
  const abriu = !!log.openedAt || s === 'opened' || s === 'clicked';
  const chegou = abriu || !!log.deliveredAt || s === 'delivered';
  if (!chegou) return { texto: 'Enviado, sem confirmação de entrega', tom: 'neutral' };
  const partes = ['Entregue'];
  if (abriu) partes.push(Number(log.openCount) > 1 ? `aberto ${log.openCount}x` : 'aberto');
  if (log.clickedAt || s === 'clicked') partes.push('clicado');
  return { texto: partes.join(' · '), tom: 'neutral' };
}
